import { Link } from 'react-router';
import { ENUM_LABELS, type OrderRequestRow, formatPersonName } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { DetailList } from '#client/components/ui/DetailList';
import { TextField } from '#client/components/ui/TextField';
import { formatDateTime, formatMoney } from '#client/lib/format';
import { routes } from '#client/lib/routes';
import { type LineSummary, describeMatchProgress } from './order-request.logic';

const TONE: Record<OrderRequestRow['status'], 'warning' | 'success' | 'neutral'> = {
  pending: 'warning',
  registered: 'success',
  declined: 'neutral',
};

export function RequestStatusBanners({ request }: { request: OrderRequestRow }) {
  return (
    <>
      {request.status === 'declined' && request.declined_reason && (
        <Banner tone="warning" title="Set aside">
          {request.declined_reason}
        </Banner>
      )}

      {request.status === 'registered' && request.patient_id && (
        <Banner tone="success" title="Registered">
          This became a visit.{' '}
          <Link to={routes.patient(request.patient_id)}>Open the patient record</Link>.
        </Banner>
      )}
    </>
  );
}

interface RequestActionsProps {
  status: OrderRequestRow['status'];
  isBusy: boolean;
  canRegister: boolean;
  onRegister: () => void;
  onStartDecline: () => void;
  onReopen: () => void;
}

export function RequestActions({
  status,
  isBusy,
  canRegister,
  onRegister,
  onStartDecline,
  onReopen,
}: RequestActionsProps) {
  if (status === 'pending') {
    return (
      <div className="c-card__actions">
        <Button variant="primary" size="sm" disabled={isBusy || !canRegister} onClick={onRegister}>
          Register this patient
        </Button>
        <Button size="sm" disabled={isBusy} onClick={onStartDecline}>
          Set aside
        </Button>
      </div>
    );
  }
  if (status === 'declined') {
    return (
      <Button size="sm" loading={isBusy} onClick={onReopen}>
        Put back in the queue
      </Button>
    );
  }
  return null;
}

interface DeclineFormProps {
  reason: string;
  isBusy: boolean;
  onReasonChange: (reason: string) => void;
  onDecline: () => void;
}

export function DeclineForm({ reason, isBusy, onReasonChange, onDecline }: DeclineFormProps) {
  return (
    <div className="c-requisition__reason">
      <TextField
        label="Why is it being set aside?"
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
        helpText="Required. An inbox where things vanish without a reason is one nobody trusts."
        required
      />
      <Button variant="danger" disabled={!reason.trim() || isBusy} onClick={onDecline}>
        Set it aside
      </Button>
    </div>
  );
}

export function RequestDetails({ request }: { request: OrderRequestRow }) {
  return (
    <DetailList
      items={[
        {
          label: 'Status',
          value: (
            <Badge tone={TONE[request.status]}>
              {ENUM_LABELS.orderRequestStatus[request.status]}
            </Badge>
          ),
        },
        { label: 'Contact', value: request.phone_number ?? 'Not given' },
        {
          label: 'Age',
          value: request.age_year === null ? 'Not given' : `${request.age_year} years`,
        },
        {
          label: 'Gender',
          value: request.gender ? ENUM_LABELS.gender[request.gender] : 'Not given',
        },
        { label: 'Referred by', value: request.referred_by ?? 'Not given' },
        { label: 'How it arrived', value: request.source ?? 'Not recorded' },
        {
          label: 'Taken by',
          value: `${formatPersonName(request.created_by)} · ${formatDateTime(request.created_at)}`,
        },
        ...(request.note ? [{ label: 'Note', value: request.note, wide: true }] : []),
      ]}
    />
  );
}

interface RequestedTestsCardProps {
  lines: OrderRequestRow['lines'];
  summary: LineSummary;
}

export function RequestedTestsCard({ lines, summary }: RequestedTestsCardProps) {
  return (
    <Card title="Tests asked for" subtitle={describeMatchProgress(summary)}>
      <ul className="c-order-request-lines">
        {lines.map((line) => (
          <li key={line.id} className="c-order-request-line">
            <span className="c-order-request-line__written">{line.requested_text}</span>
            {line.lab_test ? (
              <>
                <span className="c-order-request-line__arrow" aria-hidden>
                  →
                </span>
                <span>
                  {line.lab_test.name}
                  <span className="c-inventory__code">{line.lab_test.code}</span>
                </span>
                <span className="c-order-request-line__price">
                  {formatMoney(line.lab_test.price)}
                </span>
              </>
            ) : (
              <span className="c-order-request-line__unmapped">Not matched to a test yet</span>
            )}
          </li>
        ))}
      </ul>

      {summary.matchedCount > 0 && (
        <dl className="c-summary">
          <div className="c-summary__row c-summary__row--total">
            <dt>At today&rsquo;s list prices</dt>
            <dd>{formatMoney(summary.total)}</dd>
          </div>
        </dl>
      )}
    </Card>
  );
}
